/*
  Elite Auto Gear: sold-out products last on collection pages.

  Shopify has no "sold out last" sort for collections, so this re-orders the grid in the
  browser: in-stock products first, sold-out products after them, across every page,
  with the shopper's sort and filters kept. The URL never changes.

  How: each page of the grid is rebuilt from two lists that Shopify's own filter can give,
  "in stock" (filter.v.availability=1) and "out of stock" (filter.v.availability=0),
  fetched with the Section Rendering API (only the grid section, not the whole page).
    page p shows items (p-1)*per .. p*per of [in-stock list, then sold-out list]
  The total and page count stay the same, so the theme's pagination is unchanged.

  Steps aside when: the shopper picks an Availability filter, a search engine is visiting
  (they get the normal server page), or anything fails (the normal grid just shows).
  Needs data-eag-* attributes on #product-grid / its <li>s (sections/main-collection-product-grid.liquid).
*/
(function () {
  'use strict';

  var html = document.documentElement;
  var cache = {};
  var run = 0;

  function reveal() { html.classList.remove('eag-sol-wait'); }

  function skip() {
    return /[?&]filter\.v\.availability/.test(location.search) ||
      /bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent);
  }

  // Current query without page / section_id / availability.
  function baseParams() {
    var p = new URLSearchParams(location.search);
    ['page', 'section_id'].forEach(function (k) { p.delete(k); });
    Array.from(p.keys()).forEach(function (k) { if (k.indexOf('filter.v.availability') === 0) p.delete(k); });
    return p;
  }

  function fetchGrid(sectionId, avail, page) {
    var p = baseParams();
    p.set('filter.v.availability', avail);
    p.set('page', page);
    p.set('section_id', sectionId);
    var url = location.pathname + '?' + p.toString();
    if (!cache[url]) {
      cache[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (t) {
          var doc = new DOMParser().parseFromString(t, 'text/html');
          var g = doc.getElementById('product-grid');
          return {
            total: g ? +g.getAttribute('data-eag-total') || 0 : 0,
            items: g ? Array.prototype.slice.call(g.querySelectorAll(':scope > li.grid-item')) : []
          };
        })
        .catch(function (e) { delete cache[url]; throw e; });
    }
    return cache[url];
  }

  function apply() {
    var my = ++run;
    var grid = document.getElementById('product-grid');
    if (!grid || skip() || !grid.hasAttribute('data-eag-total')) return reveal();

    var total = +grid.getAttribute('data-eag-total');
    var per = +grid.getAttribute('data-eag-per');
    var page = +grid.getAttribute('data-eag-page') || 1;
    var sectionId = grid.getAttribute('data-id');
    var current = Array.prototype.slice.call(grid.querySelectorAll(':scope > li.grid-item'));
    if (!total || !per || !sectionId || !current.length) return reveal();

    // Everything fits on one page: just move the sold-out cards to the end.
    if (total <= per) {
      current.filter(function (li) { return li.getAttribute('data-eag-av') === '0'; })
        .forEach(function (li) { grid.appendChild(li); });
      return reveal();
    }

    var start = (page - 1) * per;
    var end = Math.min(start + per, total);

    // Page 1 with nothing sold out on it is already right: it holds the first in-stock items.
    if (page === 1 && current.every(function (li) { return li.getAttribute('data-eag-av') === '1'; })) return reveal();

    // In-stock count for the current filters comes with the page (Availability facet);
    // knowing it up front lets every request below run at the same time.
    var known = grid.getAttribute('data-eag-instock');
    var plan = known !== null && known !== '' ? Promise.resolve(+known)
      : fetchGrid(sectionId, 1, 1).then(function (r) { return r.total; });

    plan.then(function (I) {
      var reqs = [];
      // In-stock part of this page: in-stock page p holds exactly items start..start+per of that list.
      reqs.push(start < I ? fetchGrid(sectionId, 1, page) : Promise.resolve({ items: [] }));
      var inCount = Math.max(0, Math.min(end, I) - start);
      var need = (end - start) - inCount;
      // Sold-out part: offset into the sold-out list, and the 1 or 2 pages of it that cover it.
      var k0 = Math.max(0, start - I);
      var q1 = Math.floor(k0 / per) + 1;
      var q2 = Math.floor((k0 + Math.max(need, 1) - 1) / per) + 1;
      if (need > 0) {
        reqs.push(fetchGrid(sectionId, 0, q1));
        if (q2 !== q1) reqs.push(fetchGrid(sectionId, 0, q2));
      }
      return Promise.all(reqs).then(function (r) {
        var items = r[0].items.slice(0, inCount);
        if (need <= 0) return items;
        var sold = r.slice(1).reduce(function (a, x) { return a.concat(x.items); }, []);
        var from = k0 - (q1 - 1) * per;
        return items.concat(sold.slice(from, from + need));
      });
    }).then(function (items) {
      if (my !== run) return;
      // A product with both in-stock and sold-out variants can be in both lists: show it once.
      var seen = {};
      items = items.filter(function (li) {
        var id = li.getAttribute('data-eag-id');
        if (seen[id]) return false;
        seen[id] = true;
        return true;
      });
      if (!items.length) return reveal();
      var frag = document.createDocumentFragment();
      items.forEach(function (li) { frag.appendChild(document.importNode(li, true)); });
      grid.replaceChildren(frag);
      document.dispatchEvent(new CustomEvent('eag:grid-reordered'));
      reveal();
    }).catch(function () { reveal(); });
  }

  // Re-run whenever the theme redraws the grid (filters, sort, back/forward).
  function hookFacets() {
    // facets.js declares the class at top level: a global binding, not a window property.
    var F = typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : null;
    if (!F || F.__eagSol) return !!F;
    var orig = F.renderProductGridContainer;
    F.renderProductGridContainer = function () {
      if (!skip()) html.classList.add('eag-sol-wait');
      var out = orig.apply(this, arguments);
      // Cached results render before the theme updates the address bar: wait a tick.
      setTimeout(apply, 0);
      return out;
    };
    F.__eagSol = true;
    return true;
  }

  function start() {
    if (!hookFacets()) setTimeout(hookFacets, 500);
    apply();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
